'use client';

import { ElementProps } from '@/components/sections/builder/types';
import React from 'react';
import {
  ProductDetailQuantityElementDataType,
  ProductDetailTitleElementDataType,
} from '@/components/sections/builder/elements/body/product-detail/type';
import { Hash, Share2 } from 'lucide-react';
import { useBuilderStore } from '@/stores/builder';
import { shallow } from 'zustand/vanilla/shallow';
import { IProduct, ProductItem } from '@/types/product';
import { Minus, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCartProduct } from '@/stores/cart';
import { Input } from '@/components/ui/input';
import { useProductStore } from '@/stores/product';
import { cn } from '@/lib/utils';
import { toast } from '@/components/ui/use-toast';
import InputSelect from '@/components/sections/builder/settings/input-select';
import { Switch } from '@/components/ui/switch';

const defaultData: ProductDetailQuantityElementDataType = {
  name: 'Quantity',
  qty: 0,
};

export const ElementContent = ({ element, preview }: ElementProps) => {
  const parentData = (element.parentData || {}) as IProduct;
  const data = (element.data || {}) as ProductDetailTitleElementDataType;

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
    }),
    shallow
  );
  const cartProduct = useCartProduct(
    (state) => ({
      quantity: state.quantity,
      setQuantity: state.setQuantity,
    }),
    shallow
  );
  const productStore = useProductStore(
    (state) => ({
      quantity: state.quantity,
      setQuantity: state.setQuantity,
      allSelectedVariants: state.allSelectedVariants,
    }),
    shallow
  );

  const themeContent = preview ? builder.theme : element.theme;

  const productItems = (parentData.product_items || []) as ProductItem[];
  const productVariants = parentData.variants;
  const selectedVariant = productStore.allSelectedVariants;
  const availableQty = productItems.reduce((acc, item) => acc + Math.max(Number(item.stock), 0), 0);
  const quantity = productStore.quantity;
  const cartQty = cartProduct.quantity;

  const incrementQty = React.useCallback(() => {
    const newQty = cartQty + 1;
    if (newQty <= availableQty) {
      cartProduct.setQuantity(newQty);
    }
  }, [cartQty, availableQty, cartProduct]);
  const decrementQty = React.useCallback(() => {
    const newQty = cartQty - 1;
    if (newQty >= 1) {
      cartProduct.setQuantity(newQty);
    }
  }, [cartQty, cartProduct]);
  const handleQtyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = parseInt(e.target.value.replace(/\D/g, ''), 10) || 1;
    const maxQty = Math.min(availableQty, quantity);
    const clampedQty = Math.max(1, Math.min(numericValue, maxQty));

    cartProduct.setQuantity(clampedQty);
  };

  // React.useEffect(() => {
  //   if (cartQty === 0) {
  //     cartProduct.setQuantity(quantity > 0 ? 1 : 0);
  //   }
  // }, [quantity, cartProduct]);

  React.useEffect(() => {
    productStore.setQuantity(availableQty);
  }, [parentData.id]);

  const handleCopyLink = async () => {
    const url = window.location.href;
    await navigator.clipboard.writeText(url);
    toast({
      description: 'Copied to clipboard',
      variant: 'success',
      duration: 1000,
    });
  };

  return (
    <>
      {themeContent?.productCard?.positionButtonAddToCart !== 'fixed' ? (
        <>
          {(data.qtyStyle === 'style-1' || !data.qtyStyle) && (
            <div className='flex flex-col'>
              <label className='mb-1 block text-sm font-medium'>Quantity</label>
              <div className='mt-2 flex items-center space-x-2'>
                <div className='flex items-center space-x-6 rounded-lg border p-2'>
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={decrementQty}
                    className='theme_button_outline_modal_product_detail disabled:cursor-not-allowed'
                    disabled={cartQty <= 1 || !!(productVariants && !selectedVariant)}
                  >
                    <Minus className='h-2 w-2' />
                  </Button>
                  <Input
                    id='input-qty-product-detail'
                    type='text'
                    value={cartQty}
                    onChange={handleQtyChange}
                    max={quantity}
                    min={1}
                    className={cn(
                      'w-[60px] border-none text-center font-semibold focus-visible:ring-0',
                      data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                      (data.textStyle === 'subtitle' || !data.textStyle) &&
                        'text-sm md:text-base lg:text-lg',
                      data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base'
                    )}
                    disabled={cartQty === 0 || !!(productVariants && !selectedVariant)}
                  />
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={incrementQty}
                    className='theme_button_outline_modal_product_detail'
                    disabled={
                      cartQty === 0 ||
                      cartQty >= quantity ||
                      !!(productVariants && !selectedVariant)
                    }
                  >
                    <Plus className='h-2 w-2' />
                  </Button>
                </div>
                <div className='rounded-lg p-2'>
                  {data.showShareButton && (
                    <Button
                      variant='outline'
                      size='icon'
                      onClick={handleCopyLink}
                      className='theme_button_outline_modal_product_detail '
                    >
                      <Share2 className='h-5 w-5 ' />
                    </Button>
                  )}
                </div>
              </div>
              <div className='mt-4 flex gap-2 text-sm text-gray-500'>
                {themeContent?.productCard?.showQtySold && (
                  <span className='border-r border-gray-500 pr-2'>{parentData?.qty_sold} sold</span>
                )}
                <span>Stok : {selectedVariant ? quantity : availableQty}</span>
              </div>
            </div>
          )}

          {data.qtyStyle === 'style-2' && (
            <div className='flex flex-col'>
              <div className='mt-4 flex gap-2 text-sm text-gray-500'>
                {themeContent?.productCard?.showQtySold && (
                  <span className='border-r border-gray-500 pr-2'>{parentData?.qty_sold} sold</span>
                )}
                <span>Remaining Stok : {selectedVariant ? quantity : availableQty}</span>
              </div>
              <div className='mt-2 flex items-center space-x-2'>
                <div className='flex items-center p-2'>
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={decrementQty}
                    className='theme_button_outline_modal_product_detail h-12 w-10 rounded-full disabled:cursor-not-allowed'
                    disabled={cartQty <= 1 || !!(productVariants && !selectedVariant)}
                  >
                    <Minus className='h-6 w-6' />
                  </Button>
                  <Input
                    id='input-qty-product-detail'
                    type='text'
                    value={cartQty}
                    onChange={handleQtyChange}
                    max={quantity}
                    min={1}
                    className={cn(
                      'w-[60px] border-none text-center font-semibold focus-visible:ring-0',
                      data.textStyle === 'body' && 'text-base md:text-lg lg:text-xl',
                      (data.textStyle === 'subtitle' || !data.textStyle) &&
                        'text-sm md:text-base lg:text-lg',
                      data.textStyle === 'uppercase' && 'text-xs uppercase md:text-sm lg:text-base'
                    )}
                    disabled={cartQty === 0 || !!(productVariants && !selectedVariant)}
                  />
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={incrementQty}
                    className='theme_button_outline_modal_product_detail h-12 w-10 rounded-full'
                    disabled={
                      cartQty === 0 ||
                      cartQty >= quantity ||
                      !!(productVariants && !selectedVariant)
                    }
                  >
                    <Plus className='h-6 w-6' />
                  </Button>
                </div>
                {data.showShareButton && (
                  <Button
                    variant='outline'
                    size='icon'
                    onClick={handleCopyLink}
                    className='theme_button_outline_modal_product_detail h-12 w-10 rounded-full p-2'
                  >
                    <Share2 className='h-6 w-6' />
                  </Button>
                )}
              </div>
            </div>
          )}
        </>
      ) : (
        <div className='flex gap-2 text-sm text-gray-500'>
          {themeContent?.productCard?.showQtySold && (
            <span className='border-r border-gray-500 pr-2'>{parentData?.qty_sold} sold</span>
          )}
          <span>Remaining Stok : {selectedVariant ? quantity : availableQty}</span>
        </div>
      )}
    </>
  );
};

export const ElementPreviewIcon = Hash;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
    }),
    shallow
  );

  const data = (element.data || defaultData) as ProductDetailTitleElementDataType;

  return (
    <React.Fragment>
      <div className='my-4 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Text Style</h4>
        <InputSelect
          defaultValue={data.textStyle || 'subtitle'}
          options={[
            { label: 'Body', value: 'body' },
            { label: 'Subtitle', value: 'subtitle' },
            { label: 'UPPERCASE', value: 'uppercase' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              textStyle: v,
            })
          }
        />
      </div>
      <div className='my-4 grid grid-cols-2 items-center gap-2'>
        <h4 className=''>Quantity Style</h4>
        <InputSelect
          defaultValue={data.qtyStyle || 'style-1'}
          options={[
            { label: 'Style 1', value: 'style-1' },
            { label: 'Style 2', value: 'style-2' },
          ]}
          onChange={(v) =>
            builder.setChildData(element.parentId as string, element.id, {
              qtyStyle: v,
            })
          }
        />
      </div>
      <div className='my-2 flex items-center gap-2'>
        <label>Show Share Button</label>
        <Switch
          className='ml-auto'
          checked={data.showShareButton || false}
          onCheckedChange={(checked) => {
            builder.setChildData(element.parentId as string, element.id, {
              showShareButton: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
    </React.Fragment>
  );
}

const ProductDetailQuantity = {
  type: 'qty_button',
  group: 'product_detail',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default ProductDetailQuantity;
